import { useMemo } from 'react'
import { validateLatinCharacters } from '../utils/latinCharacterValidation'

export const useLatinCharacterValidation = (
  value: string,
  errorMessage: string,
  allowedSymbols?: string[]
) => {
  const error = useMemo(() => {
    return validateLatinCharacters(value, errorMessage, allowedSymbols)
  }, [value, errorMessage, allowedSymbols])

  return {
    error,
    isValid: error === null
  }
}

export interface AddressFields {
  street?: string
  street2?: string
  streetNumber?: string
  subDivision?: string
  postCode?: string
  poBox?: string
  city?: string
}

export interface AddressErrors {
  street?: string
  street2?: string
  streetNumber?: string
  subDivision?: string
  postCode?: string
  poBox?: string
  city?: string
}

/**
 * Hook to validate multiple address fields for Latin characters
 * @param fields - Object containing address field values
 * @param errorMessage - The error message to display when validation fails
 * @param allowedSymbols - Optional array of additional symbols to allow beyond the default set
 * @returns Object containing validation errors for each field
 */
export const useAddressLatinCharacterValidation = (
  fields: AddressFields,
  errorMessage: string,
  allowedSymbols?: string[]
): AddressErrors => {
  return useMemo(() => {
    const errors: AddressErrors = {}

    // Validate each field that has a value
    Object.entries(fields).forEach(([fieldName, value]) => {
      if (value !== undefined && value !== null) {
        const error = validateLatinCharacters(value, errorMessage, allowedSymbols)
        if (error) {
          errors[fieldName as keyof AddressErrors] = error
        }
      }
    })

    return errors
  }, [fields, errorMessage, allowedSymbols])
}

/**
 * Hook to validate a single field with Latin character validation
 * @param value - The field value to validate
 * @param errorMessage - The error message to display when validation fails
 * @param allowedSymbols - Optional array of additional symbols to allow beyond the default set
 * @returns Object containing error and isValid status
 */
export const useFieldLatinCharacterValidation = (
  value: string,
  errorMessage: string,
  allowedSymbols?: string[]
) => {
  const error = useMemo(() => {
    return validateLatinCharacters(value, errorMessage, allowedSymbols)
  }, [value, errorMessage, allowedSymbols])

  return {
    error,
    isValid: error === null
  }
}
